import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "Livermore", cities: "Downtown Livermore, Springtown, Jensen Tract, South Livermore, Sunset West" },
    { zone: "Dublin", cities: "Emerald Glen, Fallon Village, Jordan Ranch, West Dublin" },
    { zone: "Pleasanton", cities: "Downtown Pleasanton, Ruby Hill, Vintage Hills, Birdland" },
    { zone: "San Ramon", cities: "Bishop Ranch, Crow Canyon, Dougherty Valley, Twin Creeks" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Áreas que Servimos en el Tri-Valley</h2>
          <p className="text-muted-foreground">Limpieza profesional a domicilio en Livermore, Dublin, Pleasanton, San Ramon y más.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div
              key={i}
              className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar Ahora en Livermore</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const LivermoreSpanishPage = () => {
  return (
    <LocationContext.Provider value={{ slug: "livermore", name: "Livermore", type: "city", regionSlug: "alameda" }}>
      <SEO
        title="Limpieza del Hogar en Livermore, CA — Uplevel Cleaning"
        description="Servicio profesional de limpieza del hogar en Livermore, Dublin, Pleasanton y San Ramon. Precios planos sin sorpresas, reserva en 60 segundos, garantía del 200%."
        keywords="limpieza del hogar livermore ca, servicio de limpieza livermore, empleada doméstica livermore california, maid service livermore español, limpieza de casas tri-valley, limpieza dublin ca, limpieza pleasanton"
        url="https://uplevelcleaning.com/es/livermore"
        locationName="Livermore"
        regionSlug="alameda"
        lang="es"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name: "Condado de Alameda", url: "/es/alameda" },
          { name: "Livermore", url: "/es/livermore" }
        ]}
        reviews={[
          { author: "Carmen L.", rating: 5, reviewBody: "Vivo en el sur de Livermore y Uplevel Cleaning superó mis expectativas por completo. Mi casa quedó perfecta de arriba a abajo. ¡Los contraté de manera recurrente inmediatamente!", datePublished: "2026-03-18" },
          { author: "Roberto F.", rating: 5, reviewBody: "Excelente servicio de limpieza profunda en nuestra casa de Springtown. Muy profesionales, puntuales y el precio fue el mismo que me cotizaron en línea. Sin sorpresas.", datePublished: "2026-02-22" },
          { author: "Isabela V.", rating: 5, reviewBody: "He probado varios servicios en el Tri-Valley y Uplevel es definitivamente el mejor. Llegaron a tiempo, limpiaron con mucho detalle y el precio fue muy justo.", datePublished: "2026-01-15" }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <SpanishHero cityName="Livermore" regionSlug="alameda" />

          <div className="bg-primary/5 border-b border-primary/10 py-4">
            <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
              {[
                "Livermore · Dublin · Pleasanton",
                "San Ramon · Tri-Valley",
                "Garantía del 200%",
                "Precios Transparentes",
                "Reserva en 60 Segundos",
              ].map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {item}
                </span>
              ))}
            </div>
          </div>

          <SpanishWhyUs cityName="Livermore" />
          <NearbyAreas />
          <SpanishServices />

          <FrequencyDiscounts />
          <PricingCalculator />
          <SpanishTestimonials region="oakland" />
          <Guarantee />
          <SpanishFAQ cityName="Livermore" />
          <InternalLinks />
          <SpanishFinalCTA cityName="Livermore" />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default LivermoreSpanishPage;
